import React, { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Table } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import EmployeeUtilizationContext from '@totem/components/ticketing/employeeUtilization/employeeUtilizationContext';
import { EmployeeSummary } from '@totem/components/ticketing/employeeUtilization/models';
import { calculateWorkdays } from '@totem/components/ticketing/employeeUtilization/utilities';
import Hours from '@totem/components/ui_components/widgets/hours/hours';
import { isNotNull } from '@totem/utilities/common';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const EmployeeUtilizationTable = () => {
  const { rawData, loading, input, holidays } = useContext(
    EmployeeUtilizationContext,
  );

  const utilizationData = useMemo(() => {
    return isNotNull(rawData) && isNotNull(rawData.employeeSummary)
      ? rawData.employeeSummary
      : [];
  }, [rawData]);

  const calculateWorkingDays = () => {
    if (
      isNotNull(input) &&
      isNotNull(input.startTime) &&
      isNotNull(input.endTime)
    ) {
      return calculateWorkdays(input.startTime, input.endTime, holidays);
    }
    return 1;
  };

  const getEmployeeWeeklyAvailableHours = (emailAddress: string) => {
    const employee = rawData.employees.find(
      (chk) => chk.email === emailAddress,
    );
    if (isNotNull(employee)) {
      return employee.availableHours;
    }
    return 40;
  };

  const calculateCapacity = (emailAddress: string) => {
    const availableHoursPerWeek = getEmployeeWeeklyAvailableHours(emailAddress);
    const numberOfWorkDays = calculateWorkingDays();
    const capacity = (availableHoursPerWeek / 5) * numberOfWorkDays;
    return capacity;
  };

  const calculateAvailableHours = (record: EmployeeSummary) => {
    const capacity = calculateCapacity(record.email);
    const availableHours = capacity - record.totalHoursLogged;
    return availableHours;
  };

  const calculateUtilization = (record: EmployeeSummary) => {
    const capacity = calculateCapacity(record.email);
    if (record.totalHoursLogged > 0) {
      const utilization = (record.totalHoursLogged / capacity) * 100;
      return utilization;
    }
    return 0;
  };

  const calculateBillableUtilization = (record: EmployeeSummary) => {
    const capacity = calculateCapacity(record.email);
    if (capacity > 0) {
      const utilization = (record.totalBillable / capacity) * 100;
      return utilization;
    }

    return 1;
  };

  const calculateSpentTimeBillable = (record: EmployeeSummary) => {
    if (record.totalHoursLogged === 0) {
      return 0;
    }
    const percentTimeBillable =
      (record.totalBillable / record.totalHoursLogged) * 100;
    return percentTimeBillable;
  };

  const columns: ColumnProps<EmployeeSummary>[] = [
    {
      title: 'Employee',
      dataIndex: 'email',
      key: 'email',
      render: (_, record: EmployeeSummary) => {
        if (utilizationData.length > 1) {
          return (
            <Link
              to={`/dashboard/utilization/${record.email}?start=${input.startTime}&end=${input.endTime}`}
            >
              <span>{record.email}</span>
            </Link>
          );
        }

        return <span>{record.email}</span>;
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) => sortStringAscending(compA.email, compB.email),
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (_, record: EmployeeSummary) => <span>{record.department}</span>,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.department, compB.department),
    },
    {
      title: 'Capacity (Hours)',
      dataIndex: 'capacity',
      key: 'capacity',
      render: (_, record: EmployeeSummary) => (
        <Hours amount={calculateCapacity(record.email)} />
      ),
      showSorterTooltip: false,
    },
    {
      title: 'Available Hours',
      dataIndex: 'availableHours',
      key: 'availableHours',
      render: (_, record: EmployeeSummary) => (
        <Hours amount={calculateAvailableHours(record)} />
      ),
      showSorterTooltip: false,
    },
    {
      title: 'Spent Time (Hours)',
      dataIndex: 'totalHoursLogged',
      key: 'totalHoursLogged',
      render: (_, record: EmployeeSummary) => (
        <Hours amount={record.totalHoursLogged} />
      ),
      showSorterTooltip: false,
    },
    {
      title: 'Billable Time (Hours)',
      dataIndex: 'totalBillable',
      key: 'totalBillable',
      render: (_, record: EmployeeSummary) => (
        <Hours amount={record.totalBillable} />
      ),
      showSorterTooltip: false,
    },
    {
      title: 'Non-Billable Time (Hours)',
      dataIndex: 'totalUnBillable',
      key: 'totalUnBillable',
      render: (_, record: EmployeeSummary) => (
        <Hours amount={record.totalUnBillable} />
      ),
      showSorterTooltip: false,
    },
    {
      title: 'Total Utilization',
      dataIndex: 'utilization',
      key: 'utilization',
      render: (_, record: EmployeeSummary) => (
        <span>{calculateUtilization(record).toFixed(2)}%</span>
      ),
      showSorterTooltip: false,
    },
    {
      title: 'Billable Utilization',
      dataIndex: 'billableUtilization',
      key: 'billableUtilization',
      render: (_, record: EmployeeSummary) => (
        <span>{calculateBillableUtilization(record).toFixed(2)}%</span>
      ),
      showSorterTooltip: false,
    },
    {
      title: '% Time Billable',
      dataIndex: 'percentSpentTimeBillable',
      key: 'percentSpentTimeBillable',
      render: (_, record: EmployeeSummary) => (
        <span>{calculateSpentTimeBillable(record).toFixed(2)}%</span>
      ),
      showSorterTooltip: false,
    },
  ];

  return (
    <Table
      showSorterTooltip
      columns={columns}
      dataSource={utilizationData}
      loading={loading}
      pagination={{
        showSizeChanger: true,
      }}
      rowKey={(record) => record.email}
    />
  );
};

export default EmployeeUtilizationTable;
